
/* eslint-disable vue/no-mutating-props */
// `target` is the checkup page's shared form-model object (mixins/checkup-page.js
// pattern, matching components/schedule/v2/steps/*), passed by reference so this
// step can push/splice its providers list and flip no_providers in place by design.
import GuidanceCard from '../parts/GuidanceCard.vue'
import SelectedItem from '../parts/SelectedItem.vue'
import CheckCardGroup from '../parts/CheckCardGroup.vue'
import Callout from '../parts/Callout.vue'
import SearchScopeRow from '../parts/SearchScopeRow.vue'
import searchScope from '~/mixins/checkup-search-scope'

// Mirrors the MedicareCheckup platform's REL_LABELS fallback (StepProviders.vue)
// exactly, minus its "other" entry — flow's manual provider type already spells
// that out via `providerTypeOptions` below, and search results only ever offer
// the binary pcp/specialist choice (relationshipOptions).
const REL_LABELS = {
    pcp: 'Primary Care',
    specialist: 'Specialist',
    dentist: 'Dentist',
    concierge: 'Concierge',
    other: 'Other',
}
const relationshipOptions = [
    { value: 'pcp', label: 'Primary Care (PCP)', description: 'My main doctor for routine care' },
    { value: 'specialist', label: 'Specialist', description: 'I see them for a specific condition' },
]
const providerTypeOptions = [
    { value: 'pcp', label: 'Primary Care Doctor (PCP)' },
    { value: 'specialist', label: 'Specialist' },
    { value: 'dentist', label: 'Dentist' },
    { value: 'concierge', label: 'Concierge Doctor' },
    { value: 'other', label: 'Other Provider' },
]

export default {
    components: { GuidanceCard, SelectedItem, CheckCardGroup, Callout, SearchScopeRow },
    mixins: [searchScope],
    // Radius this step's search hard-coded before the client could change it
    // (see mixins/checkup-search-scope.js).
    searchDistance: 100,
    props: {
        target: { type: Object, required: true },
        // Copy is a prop so /renewal/* can reframe this step for a
        // returning client ("Are these still your…") while /checkup/*
        // keeps its first-visit wording (#656). The defaults are the
        // checkup strings verbatim, so every existing caller renders
        // byte-identical output without passing anything.
        heading: { type: String, default: 'Who are your doctors?' },
        lede: { type: String, default: 'We\'ll cross-check plans against the doctors you want to keep. Please include any primary care physician, specialist, dentist, and/or eye doctor you may see.' },
    },
    data () {
        return {
            results: [],
            searching: false,
            // Monotonic guard: with the 300ms input debounce two searches can
            // still overlap, and a slower older response must not overwrite a
            // newer one's results (or clear its spinner).
            searchSeq: 0,
            relationshipOptions,
            providerTypeOptions,
            // Add-card state: which result (by npi) has its card open, the
            // relationship choice made in it, and which of the provider's
            // locations is currently selected.
            expandedNpi: null,
            relationship: 'pcp',
            chosenLocIdx: 0,
            manual: { name: '', practice: '', type: 'pcp', specialty: '' },
        }
    },
    methods: {
        async onSearch (query) {
            const seq = ++this.searchSeq
            this.query = query
            if (!query) { this.results = []; this.searching = false; return }
            this.searching = true
            // zip and distance are the client's own picks, editable in the
            // SearchScopeRow above (mixins/checkup-search-scope.js) — not the
            // read-only target.zip_code snapshot and fixed 25 miles they were.
            const resp = await this.$api.searchProviders(
                { query, zip_code: this.zip, distance: this.distance, page: 0, page_size: 10 },
                { spinner: false },
            ).catch(() => null)
            if (seq !== this.searchSeq) { return } // stale — a newer search owns the state
            this.searching = false
            // Oracle's provider_search returns plural `locations` — kept here,
            // client-side only, so the add-card's location picker can offer
            // every office. Only the ONE the user picks is ever written onto
            // target.providers (see add() below) — the plural list itself
            // must never be persisted (flow/consults/serializers.py:74-81,
            // singular `location` required, no `locations`).
            this.results = resp?.results || []
        },
        isAdded (r) { return (this.target.providers || []).some(p => p.npi === r.npi) },
        // Click a result → open its add-card (relationship + location, then confirm).
        openCard (r) {
            if (this.isAdded(r)) { return }
            this.expandedNpi = this.expandedNpi === r.npi ? null : r.npi
            this.relationship = 'pcp'
            this.chosenLocIdx = 0
        },
        add (r) {
            if (this.isAdded(r)) { return }
            const locs = r.locations || []
            const loc = locs[this.chosenLocIdx] || locs[0] || null
            // Whitelisted to match the Provider serializer exactly (flow/consults/
            // serializers.py:74-81, singular `location` required) — raw `locations`
            // (plural) must never be persisted. If a result somehow has no location
            // at all, fall back to the ManualProvider shape so the save still
            // succeeds instead of silently losing npi/lapro_id to pydantic's
            // Provider/ManualProvider Union coercion.
            // Reassign rather than push() — target.providers is proxied
            // through to the page's reactive this.form.providers (see
            // pages/checkup/doctors.vue's formWithZip setter), and Vue's deep
            // watcher in mixins/survey-form.js hands the SAME array reference
            // as both newValue/oldValue on an in-place mutation, so its
            // `_.isEqual(newValue, oldValue)` guard would silently swallow
            // the change and nothing would autosave.
            this.target.providers = [
                ...this.target.providers,
                loc
                    ? { npi: r.npi, name: r.name, lapro_id: r.lapro_id, practice: r.practice, specialty: this.addCardSpecialty(r), location: loc, relationship: this.relationship }
                    : { name: r.name, specialty: this.addCardSpecialty(r), concierge: false, relationship: this.relationship },
            ]
            this.setOptOut(false)
            this.results = []
            this.expandedNpi = null
            this.$refs.card.clearSearch()
        },
        // The add-card's relationship pick backfills `specialty` too, when
        // Oracle didn't supply one (mirrors the REL_LABELS fallback) — the
        // relationship itself is persisted separately (see add() above).
        addCardSpecialty (r) {
            return r.specialty || REL_LABELS[this.relationship] || null
        },
        addManual (close) {
            if (!this.manual.name.trim()) { return }
            const specialty = this.manual.specialty.trim() || REL_LABELS[this.manual.type] || null
            const practice = this.manual.practice.trim()
            // flow's Provider.practice is a BOOLEAN group-practice flag from
            // Oracle (flow/consults/serializers.py:74-81), not a string — and
            // ManualProvider ({name, specialty, concierge, relationship}) has
            // no separate slot for a free-text practice/group name either.
            // Rather than drop what the user typed, fold it into the
            // specialty text.
            this.target.providers = [
                ...this.target.providers,
                {
                    name: this.manual.name.trim(),
                    specialty: practice ? [specialty, practice].filter(Boolean).join(' — ') : specialty,
                    concierge: this.manual.type === 'concierge',
                    relationship: this.manual.type,
                },
            ]
            this.manual = { name: '', practice: '', type: 'pcp', specialty: '' }
            this.setOptOut(false)
            close()
        },
        remove (i) { this.target.providers = this.target.providers.filter((_, idx) => idx !== i) },
        setOptOut (v) { this.$set(this.target, 'no_providers', v) },
        onClearAndOptOut () {
            this.target.providers = []
            this.setOptOut(true)
        },
        // Saved-row sub line: specialty in caps (platform parity #598 gap 3),
        // e.g. "ALLERGY/IMMUNOLOGY". NPI is intentionally never displayed.
        specialtyFor (p) { return p.specialty ? p.specialty.toUpperCase() : null },
        // Saved-row badge: the platform shows the user's chosen relationship
        // ("Primary Care"/"Specialist"/etc.) here, now backed by the real
        // `relationship` field on Provider/ManualProvider (flow/consults/
        // serializers.py). A legacy/loaded provider saved before that field
        // existed has no `relationship`, so it falls back to specialty like
        // before — but that fallback is, by construction, the same text
        // already shown (uppercased) on the sub line above it (see
        // specialtyFor()), so surfacing it again as a badge would be a pure
        // duplicate. Omit the badge whenever it would just repeat that line.
        pillFor (p) {
            const label = p.relationship ? (REL_LABELS[p.relationship] || p.relationship) : (p.specialty || null)
            if (!label) { return null }
            const sub = this.specialtyFor(p)
            return sub && label.toUpperCase() === sub ? null : label
        },
        formatLocation (l) {
            if (!l) { return '' }
            const cityStateZip = [[l.city, l.state].filter(Boolean).join(', '), l.zip_code].filter(Boolean).join(' ')
            return [l.address, cityStateZip].filter(Boolean).join(', ')
        },
        // Result-row sub line, matching the platform's format exactly:
        // "SPECIALTY · N locations" (multi-office) or "SPECIALTY · address ·
        // X.X mi" (single office, distance rounded to 1 decimal) — never the
        // stored singular `location`, this reads the raw search result's
        // plural `locations`.
        resultMeta (r) {
            const specialty = r.specialty ? r.specialty.toUpperCase() : ''
            const locs = r.locations || []
            let where = ''
            if (locs.length > 1) {
                where = `${locs.length} locations`
            } else if (locs[0]) {
                where = this.formatLocation(locs[0])
                if (locs[0].distance != null) { where += ` · ${Number(locs[0].distance).toFixed(1)} mi` }
            }
            return [specialty, where].filter(Boolean).join(' · ')
        },
        addrFor (p) {
            return p.location ? this.formatLocation(p.location) : (p.concierge ? 'Concierge / subscription-fee doctor' : 'Added manually')
        },
    },
}
